import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Calendar, XIcon } from '../icon/icon';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { useState } from 'react';
import { InputBtn } from '../items/inputBtn';

interface SearchConditionProps {
  startDate: string;
  endDate: string;
  isDefaultRange?: boolean; // 기본 범위 여부 추가
}

interface JobSearchConditionProps {
  handleSearchClose: () => void;
  handleSearchSubmit: (condition: SearchConditionProps) => void; // 수정된 타입
}

export const JobSearchCondition = ({
  handleSearchClose,
  handleSearchSubmit,
}: JobSearchConditionProps) => {
  const today = new Date();
  const { handleSubmit, control } = useForm();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const onSubmit = (data: any) => {
    let startDateToUse = startDate;
    let endDateToUse = endDate;

    let isDefaultRange = false;

    // 날짜가 선택되지 않은 경우 기본 날짜 범위 설정
    if (!startDateToUse && !endDateToUse) {
      startDateToUse = new Date('2020-01-01'); // 기본 시작 날짜
      endDateToUse = new Date('2222-12-31'); // 기본 종료 날짜
      isDefaultRange = true; // 기본 범위임을 표시
    }

    startDateToUse = new Date(startDateToUse!.getTime() + 9 * 60 * 60 * 1000);
    endDateToUse = new Date(endDateToUse!.getTime() + 9 * 60 * 60 * 1000);

    handleSearchSubmit({
      startDate: startDateToUse!.toISOString().substring(0, 10), // Non-null assertion 사용
      endDate: endDateToUse!.toISOString().substring(0, 10), // Non-null assertion 사용
      isDefaultRange, // 기본 범위 정보 전달
    });
  };

  return (
    <Container>
      <motion.div
        className="wrapper"
        initial={{ y: '100%' }}
        animate={{ y: '0%' }}
        exit={{ y: '100%' }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 40,
        }}
      >
        <div className="overlay" onClick={handleSearchClose}></div>
        <div className="wrap-shape">
          <div className="header">
            <span>조회 조건</span>
            <div className="icon-box" onClick={handleSearchClose}>
              <XIcon />
            </div>
          </div>
          <div className="body">
            <div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <DatePickerContainer>
                  <div className="inner">
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={value}
                          startDate={startDate ?? undefined}
                          selectsStart
                          onChange={(date: Date | null) => {
                            setStartDate(date);
                            onChange(date);
                          }}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                  <span>~</span>
                  <div className="inner">
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={value}
                          endDate={endDate ?? undefined}
                          selectsStart
                          onChange={(date: Date | null) => {
                            setEndDate(date);
                            onChange(date);
                          }}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                </DatePickerContainer>

                <InputBtn name="검색" type="submit" />
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </Container>
  );
};

// style
const Container = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  position: relative;
  .wrapper {
    width: 100%;
    height: 100%;
    .wrap-shape {
      background: ${(props) => props.theme.textWhiteColor};
      border-radius: 2rem 2rem 0 0;
      overflow: hidden;
      padding: 0.8rem 2.2rem;
      height: 23.7rem;
      width: 100%;
      position: absolute;
      bottom: 0;
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        span {
          font-size: 1.7rem;
          font-weight: 600;
        }
        .icon-box {
          display: flex;
          transform: translateX(1.6rem);
        }
      }
      .body {
        form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 1.2rem;
          margin-top: 0.9rem;
          input[type='text'] {
            padding: 1.4rem 1.8rem 1.5rem;
            border-radius: 0.8rem;
            border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
            &::placeholder {
              color: ${(props) => props.theme.searchPlaceholderColor};
            }
          }
          .on-off-btn {
            display: flex;
            align-items: center;
            justify-content: space-between;
            .title {
              margin-bottom: 0;
              font-size: 1.4rem;
            }
          }
        }
      }
    }
  }
`;

const CustomDatePicker = styled(DatePicker)``;

export const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  span {
    margin: 0 1rem;
    font-size: 1.4rem;
    font-weight: 400;
    transform: translateY(1px);
    color: ${(props) => props.theme.joinLabelColor};
  }
  .inner {
    position: relative;
    .search-input {
      padding: 1.4rem 5rem 1.5rem 1.75rem;
      max-width: 15rem;
    }
    .react-datepicker {
      font-size: 1.2rem;
      font-family: 'SpoqaHanSansNeo', sans-serif;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 1.25rem;
      transform: translateY(-50%);
    }
  }
`;
